import { useCallback, useMemo, useState } from 'react';
import * as XLSX from 'xlsx-js-style';
import { Link } from 'react-router-dom';
import { fetchPurchaseShippingReport } from '../../api/reports';
import {
  PageHeader,
  PageLoading,
  StatusBanner,
  inputClassName,
  labelClassName,
  panelClassName,
  primaryButtonClassName,
  secondaryButtonClassName,
  tableCellClassName,
  tableHeadClassName,
} from '../../components/ui';

function todayYmd() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
}

function formatCellDate(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' });
  } catch {
    return String(value);
  }
}

const PAGE_SIZE = 25;

export default function PurchaseShippingReportPage({ token }) {
  const [dateMode, setDateMode] = useState('all');
  const [dateFrom, setDateFrom] = useState(todayYmd());
  const [dateTo, setDateTo] = useState(todayYmd());
  const [status, setStatus] = useState('all');
  const [loading, setLoading] = useState(false);
  const [statusMsg, setStatusMsg] = useState({ type: 'idle', message: '' });
  const [payload, setPayload] = useState(null);
  const [page, setPage] = useState(1);

  const rows = payload?.rows || [];
  const headers = payload?.headers || [];
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageRows = rows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const rangeStart = rows.length === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(safePage * PAGE_SIZE, rows.length);

  const canRun = useMemo(() => {
    if (dateMode === 'one_day') return Boolean(dateFrom);
    if (dateMode === 'custom') return Boolean(dateFrom && dateTo);
    return true;
  }, [dateMode, dateFrom, dateTo]);

  const loadReport = useCallback(async () => {
    if (!canRun) {
      setStatusMsg({
        type: 'error',
        message:
          dateMode === 'one_day'
            ? 'Select a day.'
            : 'Select From and To dates.',
      });
      return;
    }

    setLoading(true);
    setStatusMsg({ type: 'idle', message: '' });
    try {
      const result = await fetchPurchaseShippingReport(token, {
        dateMode,
        dateFrom: dateMode === 'all' ? undefined : dateFrom,
        dateTo:
          dateMode === 'all'
            ? undefined
            : dateMode === 'one_day'
              ? dateFrom
              : dateTo,
        status,
      });
      setPayload(result);
      setPage(1);
      setStatusMsg({
        type: 'success',
        message: `Loaded ${result.total?.toLocaleString('en-IN') || 0} row(s).`,
      });
    } catch (error) {
      setPayload(null);
      setStatusMsg({
        type: 'error',
        message: error.message || 'Unable to load purchase shipping report.',
      });
    } finally {
      setLoading(false);
    }
  }, [token, dateMode, dateFrom, dateTo, status, canRun]);

const COLUMN_WIDTHS = {
  Date: 14,
  'IN ASIN': 16,
  'Com ASIN': 16,
  'Product Description': 52,
  'Ordered Qty.': 14,
  'Shipping Qty': 14,
  'Unit Price ($)': 16,
  'Total Amount $': 16,
  'Website Order #': 28,
  'USA Tracking': 28,
  'HAWB #': 16,
  'Batch ID': 16,
  'HSN Code': 14,
  'IND (AMAZON) Order id': 28,
  Name: 24,
  Address: 46,
  City: 18,
  State: 20,
  'Pin Code': 12,
  'Phone #': 16,
  'Dummy Last Mile': 20,
  'Last Mile Tracking #': 24,
  'Courier Name': 18,
  'Weight  (Gm)': 14,
  'Ref BPE ID': 14,
};

const MONEY_HEADERS = new Set(['Unit Price ($)', 'Total Amount $']);
const CENTER_HEADERS = new Set([
  'Date',
  'Ordered Qty.',
  'Shipping Qty',
  'Batch ID',
  'HSN Code',
  'Pin Code',
  'Weight  (Gm)',
]);
const WRAP_HEADERS = new Set(['Product Description', 'Address']);

function cellStyle(kind, align, wrap) {
  const thin = { style: 'thin', color: { rgb: 'D0D7E2' } };
  const border = { top: thin, bottom: thin, left: thin, right: thin };
  if (kind === 'header') {
    return {
      font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 11, name: 'Calibri' },
      fill: { patternType: 'solid', fgColor: { rgb: '0B3A5B' } },
      alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
      border,
    };
  }
  return {
    font: { sz: 10, name: 'Calibri', color: { rgb: '0F172A' } },
    fill: { patternType: 'solid', fgColor: { rgb: kind === 'zebra' ? 'E7F1FA' : 'FFFFFF' } },
    alignment: { horizontal: align, vertical: 'center', wrapText: wrap },
    border,
  };
}

function downloadExcel() {
  const matrix = payload?.excelMatrix;
  if (!matrix?.length || (matrix.length < 2 && !rows.length)) {
    setStatusMsg({ type: 'error', message: 'Load the report before downloading.' });
    return;
  }

  const headerRow = matrix[0];
  const styled = matrix.map((row, rowIndex) =>
    row.map((value, colIndex) => {
      const header = headerRow[colIndex];
      const isHeader = rowIndex === 0;
      const align = MONEY_HEADERS.has(header)
        ? 'right'
        : CENTER_HEADERS.has(header)
          ? 'center'
          : 'left';
      return {
        v: value == null ? '' : value,
        t: typeof value === 'number' ? 'n' : 's',
        s: cellStyle(
          isHeader ? 'header' : rowIndex % 2 === 0 ? 'zebra' : 'plain',
          isHeader ? 'center' : align,
          isHeader || WRAP_HEADERS.has(header)
        ),
        ...(typeof value === 'number' && MONEY_HEADERS.has(header) ? { z: '#,##0.00' } : {}),
      };
    })
  );

  const descColIdx = headerRow.indexOf('Product Description');
  const addrColIdx = headerRow.indexOf('Address');

  const sheet = XLSX.utils.aoa_to_sheet(styled);
  sheet['!cols'] = headerRow.map((header) => ({
    wch: COLUMN_WIDTHS[header] || Math.max(14, String(header).length + 4),
  }));
  sheet['!rows'] = styled.map((row, rowIndex) => {
    if (rowIndex === 0) return { hpt: 34 };
    const description = String((descColIdx >= 0 ? row[descColIdx]?.v : '') || '');
    const address = String((addrColIdx >= 0 ? row[addrColIdx]?.v : '') || '');
    const lines = Math.max(
      1,
      Math.ceil(description.length / 48),
      Math.ceil(address.length / 42)
    );
    return { hpt: Math.min(60, 18 * lines) };
  });
  sheet['!autofilter'] = { ref: sheet['!ref'] };
  sheet['!views'] = [
    {
      state: 'frozen',
      ySplit: 1,
      xSplit: 0,
      topLeftCell: 'A2',
      activePane: 'bottomLeft',
    },
  ];

  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, sheet, 'Purchase Shipping');
  XLSX.writeFile(book, `purchase-shipping-${status}-${todayYmd()}.xlsx`);
}

  return (
    <section className="grid gap-5 min-w-0 w-full">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PageHeader
          title="Purchase Shipping report"
          subtitle="Same columns as MAR Express Excel. Date filter uses IN Amazon order date."
        />
        <Link to="/admin/reports" className={`${secondaryButtonClassName} h-9 shrink-0`}>
          ← Reports
        </Link>
      </div>

      {statusMsg.type !== 'idle' ? (
        <StatusBanner status={statusMsg} setStatus={setStatusMsg} />
      ) : null}

      <div className={`${panelClassName} px-5 py-4`}>
        <div className="grid gap-4 lg:grid-cols-12 lg:items-end">
          <label className="grid gap-1 lg:col-span-3">
            <span className={labelClassName}>Date</span>
            <select
              className={`${inputClassName} h-10`}
              value={dateMode}
              onChange={(e) => setDateMode(e.target.value)}
            >
              <option value="all">All</option>
              <option value="one_day">One day</option>
              <option value="custom">Custom</option>
            </select>
          </label>

          {dateMode === 'one_day' ? (
            <label className="grid gap-1 lg:col-span-3">
              <span className={labelClassName}>Day (IN order date)</span>
              <input
                type="date"
                className={`${inputClassName} h-10`}
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </label>
          ) : null}

          {dateMode === 'custom' ? (
            <>
              <label className="grid gap-1 lg:col-span-2">
                <span className={labelClassName}>From</span>
                <input
                  type="date"
                  className={`${inputClassName} h-10`}
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              </label>
              <label className="grid gap-1 lg:col-span-2">
                <span className={labelClassName}>To</span>
                <input
                  type="date"
                  className={`${inputClassName} h-10`}
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </label>
            </>
          ) : null}

          <label className="grid gap-1 lg:col-span-3">
            <span className={labelClassName}>Status</span>
            <select
              className={`${inputClassName} h-10`}
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="all">All</option>
              <option value="shipped">Shipped</option>
              <option value="delivered">Delivered</option>
            </select>
          </label>

          <div className="flex flex-wrap gap-2 lg:col-span-3">
            <button
              type="button"
              className={`${primaryButtonClassName} h-10`}
              onClick={loadReport}
              disabled={loading || !canRun}
            >
              {loading ? 'Loading…' : 'Run report'}
            </button>
            <button
              type="button"
              className={`${secondaryButtonClassName} h-10`}
              onClick={downloadExcel}
              disabled={loading || !rows.length}
            >
              Download Excel
            </button>
          </div>
        </div>
        <p className="mt-3 text-xs text-slate-500">
          Ref BPE ID is blank (not stored in CRM). Unit Price ($) comes from procurement buying price.
        </p>
      </div>

      {loading && !rows.length ? (
        <PageLoading label="Building purchase shipping report…" />
      ) : rows.length ? (
        <div className={`${panelClassName} overflow-hidden`}>
          <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-5 py-3">
            <h3 className="text-sm font-semibold text-slate-900">
              {rows.length.toLocaleString('en-IN')} row{rows.length === 1 ? '' : 's'}
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead>
                <tr>
                  {headers.map((header) => (
                    <th key={header} className={tableHeadClassName}>
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pageRows.map((row, index) => (
                  <tr
                    key={`${row.indAmazonOrderId}-${row.inAsin}-${rangeStart + index}`}
                    className="border-t border-slate-100"
                  >
                    <td className={tableCellClassName}>{formatCellDate(row.date)}</td>
                    <td className={tableCellClassName}>{row.inAsin || '—'}</td>
                    <td className={tableCellClassName}>{row.comAsin || '—'}</td>
                    <td className={`${tableCellClassName} max-w-[16rem] truncate`} title={row.productDescription || ''}>
                      {row.productDescription || '—'}
                    </td>
                    <td className={tableCellClassName}>{row.orderedQty ?? '—'}</td>
                    <td className={tableCellClassName}>{row.shippingQty ?? '—'}</td>
                    <td className={tableCellClassName}>{row.unitPriceUsd ?? '—'}</td>
                    <td className={tableCellClassName}>{row.totalAmountUsd ?? '—'}</td>
                    <td className={tableCellClassName}>{row.websiteOrderId || '—'}</td>
                    <td className={tableCellClassName}>{row.usaTracking || '—'}</td>
                    <td className={tableCellClassName}>{row.hawb || '—'}</td>
                    <td className={tableCellClassName}>{row.batchId || '—'}</td>
                    <td className={tableCellClassName}>{row.hsnCode || '—'}</td>
                    <td className={tableCellClassName}>{row.indAmazonOrderId || '—'}</td>
                    <td className={tableCellClassName}>{row.name || '—'}</td>
                    <td className={`${tableCellClassName} min-w-[16rem] max-w-[26rem] whitespace-normal break-words leading-relaxed`} title={row.address || ''}>
                      {row.address || '—'}
                    </td>
                    <td className={tableCellClassName}>{row.city || '—'}</td>
                    <td className={tableCellClassName}>{row.state || '—'}</td>
                    <td className={tableCellClassName}>{row.pinCode || '—'}</td>
                    <td className={tableCellClassName}>{row.phone || '—'}</td>
                    <td className={tableCellClassName}>{row.dummyLastMile || '—'}</td>
                    <td className={tableCellClassName}>{row.lastMileTracking || '—'}</td>
                    <td className={tableCellClassName}>{row.courierName || '—'}</td>
                    <td className={tableCellClassName}>{row.weightGm ?? '—'}</td>
                    <td className={tableCellClassName}>{row.refBpeId || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-5 py-3 text-sm">
            <p className="text-slate-600">
              Showing {rangeStart}–{rangeEnd} of {rows.length.toLocaleString('en-IN')} row
              {rows.length === 1 ? '' : 's'}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className={secondaryButtonClassName}
                disabled={safePage <= 1}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
              >
                Previous
              </button>
              <span className="min-w-[7rem] text-center font-medium text-slate-800">
                Page {safePage} of {totalPages}
              </span>
              <button
                type="button"
                className={secondaryButtonClassName}
                disabled={safePage >= totalPages}
                onClick={() => setPage((current) => current + 1)}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      ) : payload ? (
        <div className={`${panelClassName} px-5 py-10 text-center text-sm text-slate-500`}>
          No rows for these filters.
        </div>
      ) : null}
    </section>
  );
}
